import ServiceLineGrid from './shared/ServiceLineGrid';
import PatientPicker from './shared/PatientPicker';
import PayerPicker from './shared/PayerPicker';
import Btn from './shared/Btn';
import { CLAIM_FILING_OPTIONS } from './sections/PayerRoutingSection';

const HAIRLINE = '0.4px solid var(--ab-fg)';

/**
 * @param {{ n?: string|null, label?: string, children?: any, span?: number }} props
 */
function Box14Cell({ n, label, children, span = 4 }) {
  return (
    <td
      colSpan={span}
      style={{
        border: HAIRLINE,
        verticalAlign: 'top',
        padding: 'var(--ab-space-2)',
        background: 'var(--ab-bg)',
      }}
    >
      <div style={{ position: 'relative' }}>
        {n != null && (
          <span style={{
            position: 'absolute', top: -2, left: -2,
            fontSize: 8, fontWeight: 'var(--ab-weight-bold)',
          }}>{n}</span>
        )}
        {label && (
          <div style={{
            paddingLeft: n != null ? 14 : 0,
            fontSize: 8, textTransform: 'uppercase',
            letterSpacing: 'var(--ab-tracking-label)',
            color: 'var(--ab-fg)',
            marginBottom: 4,
          }}>
            {label}
          </div>
        )}
        <div>{children}</div>
      </div>
    </td>
  );
}

/**
 * @param {{ value?: any, onChange?: (v: string) => void, disabled?: boolean, type?: string, maxLength?: number, style?: any }} props
 */
function MicroInput({ value, onChange, disabled, type = 'text', maxLength, style }) {
  return (
    <input
      type={type}
      value={value || ''}
      disabled={disabled}
      maxLength={maxLength}
      onChange={(/** @type {any} */ e) => onChange?.(e.target.value)}
      style={{
        width: '100%', padding: '4px', fontSize: 14,
        fontFamily: 'var(--ab-font-sans)',
        color: 'var(--ab-fg)',
        background: disabled ? 'var(--ab-bg-input)' : 'transparent',
        border: 'none', outline: 'none',
        ...style,
      }}
    />
  );
}

/**
 * @param {{ value?: any, options: Array<{ value: string, label: string }>, onChange?: (v: string) => void, disabled?: boolean, style?: any }} props
 */
function MicroSelect({ value, options, onChange, disabled, style }) {
  return (
    <select
      value={value || ''}
      disabled={disabled}
      onChange={(/** @type {any} */ e) => onChange?.(e.target.value)}
      style={{
        width: '100%', padding: '4px', fontSize: 12,
        fontFamily: 'var(--ab-font-sans)',
        color: 'var(--ab-fg)',
        background: disabled ? 'var(--ab-bg-input)' : 'transparent',
        border: 'none', outline: 'none',
        ...style,
      }}
    >
      {options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
    </select>
  );
}

/**
 * @param {{ value?: boolean, onChange?: (v: boolean) => void, disabled?: boolean }} props
 */
function YN({ value, onChange, disabled }) {
  return (
    <div style={{ display: 'inline-flex', gap: 8 }}>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
        <input type="radio" checked={value === true} disabled={disabled} onChange={() => onChange?.(true)} /> Y
      </label>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
        <input type="radio" checked={value === false} disabled={disabled} onChange={() => onChange?.(false)} /> N
      </label>
    </div>
  );
}

/**
 * @param {{
 *   claim: any,
 *   setField: (path: string, value: any) => void,
 *   setMany: (updates: any) => void,
 *   selectPatient: (patient: any) => void,
 *   onCreatePatient: () => void,
 *   editable: boolean,
 *   errorsByField: Map<string, any>,
 * }} props
 */
export default function ExpertGrid({
  claim, setField, setMany, selectPatient, onCreatePatient, editable, errorsByField,
}) {
  const insured = claim.insured || {};
  const patient = claim.patient || {};
  const billing = claim.billing_provider_snapshot || {};
  const facility = claim.service_facility_snapshot || {};
  const isSelf = (claim.patient_relationship_to_insured || '18') === '18';

  return (
    <div style={{
      maxWidth: 850, margin: '0 0 24px',
      background: 'var(--ab-bg)',
      border: '1px solid var(--ab-fg)',
      padding: 16,
    }}>
      <div style={{ fontSize: 10, textAlign: 'center', marginBottom: 8 }}>
        <strong>HEALTH INSURANCE CLAIM FORM</strong> — APPROVED BY NUCC 02/12 — PICA
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <colgroup>{Array.from({ length: 12 }).map((_, i) => <col key={i} />)}</colgroup>

        <tbody>
          <tr>
            <Box14Cell label="Carrier" span={8}>
              <PayerPicker
                value={claim.payer_snapshot?.payer_id}
                payerSnapshot={claim.payer_snapshot}
                onSelect={(/** @type {any} */ p) => p ? setMany({
                  payer_snapshot: { payer_id: p.payer_id, name: p.name, address_line_1: p.address_line_1 || '' },
                  ...(!claim.claim_filing_indicator ? { claim_filing_indicator: p.claim_filing_indicator || 'CI' } : {}),
                }) : setField('payer_snapshot', null)}
                density="paper"
                disabled={!editable}
              />
            </Box14Cell>
            <Box14Cell label="Carrier address" span={4}>
              <div style={{ fontSize: 11 }}>
                {claim.payer_snapshot?.address_line_1 || ''}<br />
                {claim.payer_snapshot?.city || ''} {claim.payer_snapshot?.state || ''} {claim.payer_snapshot?.zip_code || ''}
              </div>
            </Box14Cell>
          </tr>

          <tr>
            <Box14Cell n="1" label="Insurance type" span={8}>
              <MicroSelect
                value={claim.claim_filing_indicator || ''}
                options={CLAIM_FILING_OPTIONS}
                disabled={!editable}
                onChange={(v) => setField('claim_filing_indicator', v)}
              />
            </Box14Cell>
            <Box14Cell n="1a" label="Insured's I.D. number" span={4}>
              <MicroInput value={insured.member_id} disabled={!editable}
                onChange={(v) => setField('insured.member_id', v)} />
            </Box14Cell>
          </tr>

          <tr>
            <Box14Cell n="2" label="Patient's name" span={4}>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <PatientPicker
                  value={claim.patient_id}
                  patientSnapshot={claim.patient}
                  onSelect={(/** @type {any} */ p) => p ? selectPatient(p) : setMany({ patient_id: null, patient: null })}
                  onCreateNew={onCreatePatient}
                  density="paper"
                  disabled={!editable}
                />
                {editable && !claim.patient_id && (
                  <Btn kind="ghost" size="sm" icon="user-plus" onClick={onCreatePatient}>New</Btn>
                )}
              </div>
            </Box14Cell>
            <Box14Cell n="3" label="Patient DOB / sex" span={4}>
              <MicroInput type="date" value={patient.date_of_birth} disabled={!editable}
                onChange={(v) => setField('patient.date_of_birth', v)} />
              <MicroInput value={patient.gender} maxLength={1} disabled={!editable}
                onChange={(v) => setField('patient.gender', v.toUpperCase())} style={{ width: 32 }} />
            </Box14Cell>
            <Box14Cell n="4" label="Insured's name" span={4}>
              <MicroInput value={isSelf ? `${patient.first_name || ''} ${patient.last_name || ''}`.trim() : `${insured.first_name || ''} ${insured.last_name || ''}`.trim()}
                disabled={!editable || isSelf}
                onChange={(v) => {
                  const [f, ...rest] = v.split(' ');
                  setMany({ 'insured.first_name': f || '', 'insured.last_name': rest.join(' ') });
                }}
              />
            </Box14Cell>
          </tr>

          <tr>
            <Box14Cell n="5" label="Patient's address (line, city, state, ZIP, phone)" span={4}>
              <MicroInput value={patient.address_line_1} disabled={!editable}
                onChange={(v) => setField('patient.address_line_1', v)} />
              <div style={{ display: 'flex', gap: 2 }}>
                <MicroInput value={patient.city} disabled={!editable}
                  onChange={(v) => setField('patient.city', v)} />
                <MicroInput value={patient.state} maxLength={2} disabled={!editable}
                  onChange={(v) => setField('patient.state', v.toUpperCase())} style={{ width: 36 }} />
                <MicroInput value={patient.zip_code} maxLength={10} disabled={!editable}
                  onChange={(v) => setField('patient.zip_code', v)} style={{ width: 80 }} />
              </div>
              <MicroInput value={isSelf ? (insured.phone || '') : (patient.phone || '')}
                disabled={!editable}
                onChange={(v) => setField(isSelf ? 'insured.phone' : 'patient.phone', v)} />
            </Box14Cell>
            <Box14Cell n="6" label="Patient relationship" span={2}>
              <select
                value={claim.patient_relationship_to_insured || '18'}
                disabled={!editable}
                onChange={(e) => setField('patient_relationship_to_insured', e.target.value)}
                style={{ fontSize: 11, padding: 2, width: '100%' }}
              >
                <option value="18">Self</option>
                <option value="01">Spouse</option>
                <option value="19">Child</option>
                <option value="G8">Other</option>
              </select>
            </Box14Cell>
            <Box14Cell n="7" label="Insured's address (locked when Self)" span={6}>
              <MicroInput value={isSelf ? patient.address_line_1 : insured.address_line_1}
                disabled={!editable || isSelf}
                onChange={(v) => setField('insured.address_line_1', v)} />
              <MicroInput value={insured.phone || ''} disabled={!editable}
                onChange={(v) => setField('insured.phone', v)} />
            </Box14Cell>
          </tr>

          <tr>
            <Box14Cell n="9" label="Other insured's name" span={4}>
              <MicroInput value={claim.other_insured_name} disabled={!editable}
                onChange={(v) => setField('other_insured_name', v)} />
            </Box14Cell>
            <Box14Cell n="9a" label="Other insured policy or group #" span={4}>
              <MicroInput value={claim.other_insured_policy_number} disabled={!editable}
                onChange={(v) => setField('other_insured_policy_number', v)} />
            </Box14Cell>
            <Box14Cell n="9d" label="Insurance plan or program name" span={4}>
              <MicroInput value={claim.other_insured_plan_name} disabled={!editable}
                onChange={(v) => setField('other_insured_plan_name', v)} />
            </Box14Cell>
          </tr>

          <tr>
            <Box14Cell n="10a" label="Employment? (Y/N)" span={4}>
              <YN value={claim.condition_related_to?.employment} disabled={!editable}
                onChange={(v) => setField('condition_related_to.employment', v)} />
            </Box14Cell>
            <Box14Cell n="10b" label="Auto accident? (Y/N) + state" span={4}>
              <YN value={claim.condition_related_to?.auto_accident} disabled={!editable}
                onChange={(v) => setField('condition_related_to.auto_accident', v)} />
              <MicroInput value={claim.condition_related_to?.auto_state} maxLength={2}
                disabled={!editable}
                onChange={(v) => setField('condition_related_to.auto_state', v.toUpperCase())}
                style={{ width: 40 }} />
            </Box14Cell>
            <Box14Cell n="10c" label="Other accident? (Y/N)" span={4}>
              <YN value={claim.condition_related_to?.other_accident} disabled={!editable}
                onChange={(v) => setField('condition_related_to.other_accident', v)} />
            </Box14Cell>
          </tr>

          <tr>
            <Box14Cell n="11" label="Insured's policy / group / FECA #" span={6}>
              <MicroInput value={insured.group_number} disabled={!editable}
                onChange={(v) => setField('insured.group_number', v)} />
            </Box14Cell>
            <Box14Cell n="11a" label="Insured DOB / sex" span={6}>
              <MicroInput type="date" value={insured.date_of_birth} disabled={!editable}
                onChange={(v) => setField('insured.date_of_birth', v)} />
            </Box14Cell>
          </tr>

          <tr>
            <Box14Cell n="11b" label="Other claim ID (qualifier + value)" span={4}>
              <MicroInput value={insured.other_claim_id_qualifier} maxLength={2} disabled={!editable}
                onChange={(v) => setField('insured.other_claim_id_qualifier', v.toUpperCase())} style={{ width: 32 }} />
              <MicroInput value={insured.other_claim_id} disabled={!editable}
                onChange={(v) => setField('insured.other_claim_id', v)} />
            </Box14Cell>
            <Box14Cell n="11c" label="Insurance plan / program name (display)" span={4}>
              <div style={{ fontSize: 11 }}>{claim.payer_snapshot?.name || ''}</div>
            </Box14Cell>
            <Box14Cell n="11d" label="Another health benefit plan? (Y/N)" span={4}>
              <YN value={claim.has_secondary_coverage} disabled={!editable}
                onChange={(v) => setField('has_secondary_coverage', v)} />
            </Box14Cell>
          </tr>

          <tr>
            <Box14Cell n="12" label="Patient signature on file + date" span={8}>
              <label style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input type="checkbox"
                  checked={claim.release_of_information === 'Y'}
                  disabled={!editable}
                  onChange={(e) => setField('release_of_information', e.target.checked ? 'Y' : 'N')}
                /> Signature on file
              </label>
              <MicroInput type="date" value={claim.signature_date} disabled={!editable}
                onChange={(v) => setField('signature_date', v)} />
            </Box14Cell>
            <Box14Cell n="13" label="Insured signature on file" span={4}>
              <label style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input type="checkbox"
                  checked={claim.benefits_assignment === 'Y'}
                  disabled={!editable}
                  onChange={(e) => setField('benefits_assignment', e.target.checked ? 'Y' : 'N')}
                /> Signature on file
              </label>
            </Box14Cell>
          </tr>

          <tr>
            <Box14Cell n="14" label="Date of current illness/injury + qualifier" span={4}>
              <MicroInput type="date" value={claim.onset_date} disabled={!editable}
                onChange={(v) => setField('onset_date', v)} />
              <MicroInput value={claim.condition_qualifier} maxLength={3} disabled={!editable}
                onChange={(v) => setField('condition_qualifier', v)} style={{ width: 60 }} />
            </Box14Cell>
            <Box14Cell n="15" label="Other date + qualifier" span={4}>
              <MicroInput type="date" value={claim.other_date} disabled={!editable}
                onChange={(v) => setField('other_date', v)} />
              <MicroInput value={claim.other_date_qualifier} maxLength={3} disabled={!editable}
                onChange={(v) => setField('other_date_qualifier', v)} style={{ width: 60 }} />
            </Box14Cell>
            <Box14Cell n="16" label="Unable to work (from / to)" span={4}>
              <MicroInput type="date" value={claim.disability_from_date} disabled={!editable}
                onChange={(v) => setField('disability_from_date', v)} />
              <MicroInput type="date" value={claim.work_return_date} disabled={!editable}
                onChange={(v) => setField('work_return_date', v)} />
            </Box14Cell>
          </tr>

          <tr>
            <Box14Cell n="17" label="Referring provider name" span={6}>
              <MicroInput value={claim.referring_provider_name} disabled={!editable}
                onChange={(v) => setField('referring_provider_name', v)} />
            </Box14Cell>
            <Box14Cell n="17a" label="Other ID + qualifier" span={2}>
              <MicroInput value={claim.referring_provider_id_qualifier} maxLength={2} disabled={!editable}
                onChange={(v) => setField('referring_provider_id_qualifier', v.toUpperCase())} style={{ width: 36 }} />
              <MicroInput value={claim.referring_provider_other_id} disabled={!editable}
                onChange={(v) => setField('referring_provider_other_id', v)} />
            </Box14Cell>
            <Box14Cell n="17b" label="NPI" span={4}>
              <MicroInput value={claim.referring_provider_npi} maxLength={10} disabled={!editable}
                onChange={(v) => setField('referring_provider_npi', v)} />
            </Box14Cell>
          </tr>

          <tr>
            <Box14Cell n="18" label="Hospitalization (from / to)" span={4}>
              <MicroInput type="date" value={claim.hospitalization_from_date} disabled={!editable}
                onChange={(v) => setField('hospitalization_from_date', v)} />
              <MicroInput type="date" value={claim.hospitalization_to_date} disabled={!editable}
                onChange={(v) => setField('hospitalization_to_date', v)} />
            </Box14Cell>
            <Box14Cell n="19" label="Additional claim information" span={8}>
              <MicroInput value={claim.additional_claim_info} disabled={!editable}
                onChange={(v) => setField('additional_claim_info', v)} />
            </Box14Cell>
          </tr>

          <tr>
            <Box14Cell n="20" label="Outside lab? (Y/N) + $ charges" span={4}>
              <YN value={claim.outside_lab} disabled={!editable}
                onChange={(v) => setField('outside_lab', v)} />
              <MicroInput type="number" value={claim.outside_lab_charge}
                disabled={!editable}
                onChange={(v) => setField('outside_lab_charge', v)} />
            </Box14Cell>
          </tr>

          <tr>
            <Box14Cell n="21" label="Diagnoses (ICD-10) — A–L" span={12}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 4 }}>
                {(claim.diagnoses || []).slice(0, 12).map((/** @type {any} */ dx, /** @type {number} */ i) => (
                  <MicroInput
                    key={i}
                    value={dx?.diagnosis_code}
                    disabled={!editable}
                    onChange={(v) => {
                      const next = [...(claim.diagnoses || [])];
                      next[i] = { ...(next[i] || { id: `dx_${i + 1}` }), diagnosis_code: v.toUpperCase() };
                      setField('diagnoses', next);
                    }}
                    maxLength={10}
                  />
                ))}
              </div>
            </Box14Cell>
          </tr>

          <tr>
            <Box14Cell n="22" label="Resubmission code + original ref" span={8}>
              <MicroInput value={claim.claim_frequency_code} maxLength={1} disabled={!editable}
                onChange={(v) => setField('claim_frequency_code', v)} style={{ width: 32 }} />
              <MicroInput value={claim.original_reference_number} disabled={!editable}
                onChange={(v) => setField('original_reference_number', v)} />
            </Box14Cell>
            <Box14Cell n="23" label="Prior authorization #" span={4}>
              <MicroInput value={claim.prior_authorization_number} disabled={!editable}
                onChange={(v) => setField('prior_authorization_number', v)} />
            </Box14Cell>
          </tr>

          <tr>
            <Box14Cell n="24" label="Service lines" span={12}>
              <ServiceLineGrid
                lines={claim.service_lines || []}
                payerId={claim.payer_snapshot?.payer_id}
                claimRenderingNpi={(claim.service_lines || []).find((/** @type {any} */ l) => l.rendering_provider_npi)?.rendering_provider_npi || ''}
                claimDateOfService={claim.service_lines?.[0]?.service_date_from || ''}
                onChange={(/** @type {any} */ next) => setField('service_lines', next)}
                mode="paper"
                disabled={!editable}
              />
            </Box14Cell>
          </tr>

          <tr>
            <Box14Cell n="25" label="Federal Tax I.D. + qualifier" span={4}>
              <MicroInput value={billing.tax_id} disabled={!editable}
                onChange={(v) => setField('billing_provider_snapshot.tax_id', v)} />
              <MicroInput value={billing.tax_id_qualifier} maxLength={2} disabled={!editable}
                onChange={(v) => setField('billing_provider_snapshot.tax_id_qualifier', v.toUpperCase())} style={{ width: 40 }} />
            </Box14Cell>
            <Box14Cell n="26" label="Patient's account no." span={4}>
              <MicroInput value={claim.patient_account_number} disabled={!editable}
                onChange={(v) => setField('patient_account_number', v)} />
            </Box14Cell>
            <Box14Cell n="27" label="Accept assignment?" span={4}>
              <select value={claim.accept_assignment || 'A'} disabled={!editable}
                onChange={(e) => setField('accept_assignment', e.target.value)}
                style={{ fontSize: 11, padding: 2, width: '100%' }}
              >
                <option value="A">Yes (A)</option>
                <option value="C">No — clinic accepts (C)</option>
                <option value="N">No (N)</option>
              </select>
            </Box14Cell>
          </tr>

          <tr>
            <Box14Cell n="28" label="Total charge" span={6}>
              <div style={{ fontFamily: 'var(--ab-font-mono)' }}>
                ${(claim.service_lines || []).reduce((/** @type {number} */ s, /** @type {any} */ l) => s + (Number(l.charge_amount) || 0), 0).toFixed(2)}
              </div>
            </Box14Cell>
            <Box14Cell n="29" label="Amount paid" span={6}>
              <MicroInput type="number" value={claim.amount_paid} disabled={!editable}
                onChange={(v) => setField('amount_paid', v)} />
            </Box14Cell>
          </tr>

          <tr>
            <Box14Cell n="31" label="Signature of physician + date" span={4}>
              <MicroInput value={claim.provider_signature} disabled={!editable}
                onChange={(v) => setField('provider_signature', v)} />
              <MicroInput type="date" value={claim.provider_signature_date} disabled={!editable}
                onChange={(v) => setField('provider_signature_date', v)} />
            </Box14Cell>
            <Box14Cell n="32" label="Service facility (name + address)" span={4}>
              <MicroInput value={facility.organization_name} disabled={!editable}
                onChange={(v) => setField('service_facility_snapshot.organization_name', v)} />
              <MicroInput value={facility.address_line_1} disabled={!editable}
                onChange={(v) => setField('service_facility_snapshot.address_line_1', v)} />
            </Box14Cell>
            <Box14Cell n="33" label="Billing provider info & phone" span={4}>
              <MicroInput value={billing.organization_name} disabled={!editable}
                onChange={(v) => setField('billing_provider_snapshot.organization_name', v)} />
              <MicroInput value={billing.address_line_1} disabled={!editable}
                onChange={(v) => setField('billing_provider_snapshot.address_line_1', v)} />
              <MicroInput value={billing.phone} disabled={!editable}
                onChange={(v) => setField('billing_provider_snapshot.phone', v)} />
            </Box14Cell>
          </tr>

          <tr>
            <Box14Cell n="32a" label="NPI" span={2}>
              <MicroInput value={facility.npi} maxLength={10} disabled={!editable}
                onChange={(v) => setField('service_facility_snapshot.npi', v)} />
            </Box14Cell>
            <Box14Cell n="32b" label="Other ID" span={2}>
              <MicroInput value={facility.other_id} disabled={!editable}
                onChange={(v) => setField('service_facility_snapshot.other_id', v)} />
            </Box14Cell>
            <Box14Cell n="33a" label="NPI" span={2}>
              <MicroInput value={billing.npi} maxLength={10} disabled={!editable}
                onChange={(v) => setField('billing_provider_snapshot.npi', v)} />
            </Box14Cell>
            <Box14Cell n="33b" label="Other ID" span={2}>
              <MicroInput value={billing.additional_id} disabled={!editable}
                onChange={(v) => setField('billing_provider_snapshot.additional_id', v)} />
            </Box14Cell>
            <Box14Cell label="" span={4}>
              <span />
            </Box14Cell>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
